import type { CSSProperties, ElementType, ReactNode } from "react";

type TypographyVariant =
  | "display"
  | "headline"
  | "title"
  | "subtitle"
  | "subtitleSmall"
  | "body"
  | "bodySmall"
  | "caption"
  | "eyebrow"
  | "button"
  | "headlineHi"
  | "titleHi"
  | "bodyHi"
  | "captionHi";

export interface TypographyProps {
  variant?: TypographyVariant;
  color?: string;
  children: ReactNode;
  /** DOM element override. Defaults per-variant (h1/h2/h3 for headings,
   *  span for everything else). */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  numberOfLines?: number;
}

const SERIF = "var(--font-pt-serif), PT Serif, Georgia, serif";
const DEVANAGARI = "var(--font-noto-devanagari), 'Noto Sans Devanagari', sans-serif";

const variantStyles: Record<TypographyVariant, CSSProperties> = {
  display: { fontSize: 34, fontWeight: 800, lineHeight: "40px", fontFamily: SERIF, letterSpacing: -0.5 },
  headline: { fontSize: 28, fontWeight: 800, lineHeight: "34px", fontFamily: SERIF },
  title: { fontSize: 22, fontWeight: 700, lineHeight: "28px", fontFamily: SERIF },
  subtitle: { fontSize: 18, fontWeight: 700, lineHeight: "24px" },
  subtitleSmall: { fontSize: 16, fontWeight: 600, lineHeight: "22px" },
  body: { fontSize: 14, fontWeight: 400, lineHeight: "20px" },
  bodySmall: { fontSize: 12, fontWeight: 400, lineHeight: "16px" },
  caption: { fontSize: 11, fontWeight: 400, lineHeight: "14px" },
  eyebrow: { fontSize: 10, fontWeight: 700, lineHeight: "14px", letterSpacing: "1.5px", textTransform: "uppercase" },
  button: { fontSize: 15, fontWeight: 700, lineHeight: "20px" },
  headlineHi: { fontSize: 26, fontWeight: 700, lineHeight: "38px", fontFamily: DEVANAGARI },
  titleHi: { fontSize: 20, fontWeight: 700, lineHeight: "30px", fontFamily: DEVANAGARI },
  bodyHi: { fontSize: 15, fontWeight: 400, lineHeight: "24px", fontFamily: DEVANAGARI },
  captionHi: { fontSize: 12, fontWeight: 400, lineHeight: "18px", fontFamily: DEVANAGARI },
};

const variantColors: Partial<Record<TypographyVariant, string>> = {
  headline: "var(--ish-dark)",
  title: "var(--ish-dark)",
  subtitle: "var(--ish-dark)",
  subtitleSmall: "var(--ish-dark)",
  body: "var(--ish-dark)",
  bodySmall: "var(--ish-text-muted)",
  caption: "var(--ish-text-faint)",
  button: "#fff",
  eyebrow: "var(--ish-accent)",
};

const defaultElement: Record<TypographyVariant, ElementType> = {
  display: "h1",
  headline: "h1",
  title: "h2",
  subtitle: "h3",
  subtitleSmall: "h4",
  body: "p",
  bodySmall: "span",
  caption: "span",
  eyebrow: "p",
  button: "span",
  headlineHi: "h1",
  titleHi: "h2",
  bodyHi: "p",
  captionHi: "span",
};

export default function Typography({
  variant = "body",
  color,
  children,
  as,
  className,
  style,
  numberOfLines,
}: TypographyProps) {
  const Tag = as ?? defaultElement[variant];
  const clampStyle: CSSProperties | undefined = numberOfLines
    ? {
        display: "-webkit-box",
        WebkitLineClamp: numberOfLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }
    : undefined;
  return (
    <Tag
      className={className}
      style={{
        ...variantStyles[variant],
        color: color ?? variantColors[variant] ?? "var(--ish-dark)",
        ...clampStyle,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}
